import React, { useRef, useState } from "react";

import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import { useAppState } from "Context/StateContext";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import { Contact } from "Models/contact";
import { Timezone } from "Models/timezone";
import { isCompanyProfile } from "Util/validation";
import { CompanyProfileInitial } from "Util/enums";
import { ContactCreateModal, ContactModalRef } from "./ContactCreateModal";
import { Grid } from "@mui/material";
import { ContactCard } from "./ContactCard";
import { useAgentHook } from "../../hooks/useAgentHook";

type Props = {
  contacts?: Contact[];
  viewOnly?: boolean;
  onChange?: (contacts: Contact[]) => void;
  timezones?: Timezone[];
};

export const ListContactsCards = (props: Props) => {
  const { contacts, viewOnly, onChange, timezones } = props;
  const { currentUser } = useAppState();
  const { agent } = useAgentHook();

  const classes = useStyles();
  const contactModalRef = useRef<ContactModalRef>(null);

  // Armazenar o índice real do contato no array original

  const handleChange = (index: number, contactPartial: Partial<Contact>) => {
    const contactsUpdated = [...(contacts || [])];

    if (contactsUpdated && contactsUpdated.length > 0 && contactsUpdated[index]) {
      contactsUpdated[index] = {
        ...contactsUpdated[index],
        ...contactPartial,
      };
      onChange?.(contactsUpdated);
    }
  };

  const handleFieldChange = (realIndex: number) => (field: string, value: any) => {
    handleChange(realIndex, { [field]: value });
  };

  const onEdit = async (contact: Contact, realIndex: number) => {
    let { mobilePhone } = contact;

    if (!isCompanyProfile(currentUser?.company, CompanyProfileInitial?.MV)) {
      if (mobilePhone && !mobilePhone.includes("+")) mobilePhone = `+${contact?.mobilePhone}`;
    }

    try {
      const result = await contactModalRef.current?.open(agent, { ...contact, mobilePhone });

      if (result) {
        const { action, contact: updatedContact } = result;

        if (action === "update" && updatedContact) {
          handleChange(realIndex, updatedContact);
        }
        // Se action === 'cancel', não fazemos nada
      }
    } catch (error) {
      console.error("Error opening contact modal:", error);
    }
  };

  const onDelete = (contact: Contact, realIndex: number) => {
    if (contact?.id) {
      // Se tem ID, marcar como inválido (soft delete)
      handleChange(realIndex, { ...contact, valid: false });
    } else {
      // Se não tem ID, remover completamente
      const contactsUpdated = [...(contacts || [])];
      contactsUpdated.splice(realIndex, 1);
      onChange?.(contactsUpdated);
    }
  };

  const renderContacts = () => {
    if (!contacts || contacts.length === 0) return null;

    // Criar array com contatos válidos e seus índices reais
    const validContactsWithIndex = contacts
      .map((contact, realIndex) => ({ contact, realIndex }))
      .filter(({ contact }) => contact.valid)
      .sort(({ contact: contactA }, { contact: contactB }) => {
        if (contactA?.isPrimary === contactB?.isPrimary) return 0;
        if (contactA?.isPrimary) return -1;
        return 1;
      });

    return validContactsWithIndex.map(({ contact, realIndex }, displayIndex) => (
      <Grid container item xs={12} key={`contact-${realIndex}`}>
        <ContactCard
          contact={contact}
          index={realIndex} // Usar o índice real para operações
          timezones={timezones}
          viewOnly={viewOnly}
          errors={{}}
          onFieldChange={handleFieldChange(realIndex)}
          onEdit={(contact) => onEdit(contact, realIndex)}
          onDelete={(contact) => onDelete(contact, realIndex)}
          classes={classes}
        />
      </Grid>
    ));
  };

  return (
    <Grid container direction="row" spacing={2}>
      {renderContacts()}
    </Grid>
  );
};
